import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Popover,
  Paper,
} from '@mui/material';
import { AccessTime, Add, CalendarMonth } from '@mui/icons-material';
import { format } from 'date-fns';
import { patientApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import TimeInput12h from './common/TimeInput12h';

interface TimeTrackingDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  patientName?: string;
  onSuccess?: () => void;
}

function getCurrentDate(): string {
  const now = new Date();
  return now.toISOString().slice(0, 10); // YYYY-MM-DD
}

function getCurrentTime(): string {
  const now = new Date();
  return now.toTimeString().slice(0, 5); // HH:mm
}

// PHA-1252: a single manual entry longer than this is a typo, not care time
// (a 7-minute call was saved as 10608 minutes and billed 176 hours). Block it before it is saved.
const MAX_DURATION_MINUTES = 240;

// Predefined tasks for CCM time tracking
const PREDEFINED_TASKS = [
  'Chart Review',
  'Phone Call - Patient',
  'Phone Call - Caregiver',
  'Phone Call - Other Provider',
  'Care Plan Development',
  'Care Plan Review',
  'Medication Review',
  'Lab Results Review',
  'Document Review',
  'Form Submission',
  'Email Communication',
  'Survey Follow-up',
  'Care Coordination',
  'Other',
];

const TimeTrackingDialog: React.FC<TimeTrackingDialogProps> = ({
  open,
  onClose,
  patientId,
  patientName,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [selectedTask, setSelectedTask] = useState('');
  const [customTask, setCustomTask] = useState('');
  const [duration, setDuration] = useState('');
  const [beginDate, setBeginDate] = useState(getCurrentDate());
  const [beginTimeValue, setBeginTimeValue] = useState(getCurrentTime());
  // Draft state for popover editing
  const [draftDate, setDraftDate] = useState(getCurrentDate());
  const [draftTime, setDraftTime] = useState(getCurrentTime());
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const pickerOpen = Boolean(pickerAnchor);
  const durationTooLong = parseInt(duration) > MAX_DURATION_MINUTES;

  useEffect(() => {
    if (open) {
      setBeginDate(getCurrentDate());
      setBeginTimeValue(getCurrentTime());
      setDraftDate(getCurrentDate());
      setDraftTime(getCurrentTime());
    }
  }, [open]);

  const handleSubmit = async () => {
    const action = selectedTask === 'Other' ? customTask : selectedTask;
    
    if (!action || !duration || !beginDate || !beginTimeValue) {
      setError('Please fill in all required fields');
      return;
    }

    const durationMinutes = parseInt(duration);
    if (isNaN(durationMinutes) || durationMinutes <= 0) {
      setError('Please enter a valid duration');
      return;
    }
    if (durationMinutes > MAX_DURATION_MINUTES) {
      setError(`Duration cannot exceed ${MAX_DURATION_MINUTES} minutes`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Combine date and time into a Date object
      const beginDateTime = new Date(`${beginDate}T${beginTimeValue}`);

      // Validate the begin time
      if (isNaN(beginDateTime.getTime())) {
        setError('Please enter a valid begin time');
        setLoading(false);
        return;
      }

      // Calculate end time based on begin time and duration
      const endDateTime = new Date(beginDateTime.getTime() + durationMinutes * 60 * 1000);

      // Ensure patient-user link exists before logging
      await patientApi.checkPatientIdAvailable(patientId);

      await patientApi.logAction({
        action: action,
        action_begin_ts: beginDateTime.toISOString(),
        action_end_ts: endDateTime.toISOString(),
        action_user: user?.name || user?.email || 'Unknown User',
        patient_id: patientId,
        details: { source: 'manual', ...(notes ? { notes } : {}) },
      });

      console.log('✅ Time entry logged successfully:', {
        action,
        duration: durationMinutes + ' minutes',
        patient: patientId,
        start: beginDateTime.toISOString(),
        end: endDateTime.toISOString(),
        notes: notes || 'none'
      });

      // Reset form
      setSelectedTask('');
      setCustomTask('');
      setDuration('');
      setBeginDate(getCurrentDate());
      setBeginTimeValue(getCurrentTime());
      setNotes('');
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (err: any) {
      console.error('❌ Failed to log time activity:', err);
      
      // Show user-friendly error
      if (err.message?.includes('404') || err.message?.includes('Endpoint not found')) {
        setError('Time tracking endpoint not available. Please contact your administrator.');
      } else {
        setError(err.message || 'Failed to log activity. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPicker = (event: React.MouseEvent<HTMLElement>) => {
    setDraftDate(beginDate);
    setDraftTime(beginTimeValue);
    setPickerAnchor(event.currentTarget);
  };

  const handleSavePicker = () => {
    setBeginDate(draftDate);
    setBeginTimeValue(draftTime);
    setPickerAnchor(null);
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedTask('');
      setCustomTask('');
      setDuration('');
      setBeginDate(getCurrentDate());
      setBeginTimeValue(getCurrentTime());
      setNotes('');
      setError(null);
      setPickerAnchor(null);
      onClose();
    }
  };

  const displayDateTime = (() => {
    try {
      const dt = new Date(`${beginDate}T${beginTimeValue}`);
      if (isNaN(dt.getTime())) return '';
      return format(dt, 'MMM d, yyyy, h:mm a');
    } catch {
      return '';
    }
  })();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <AccessTime />
          <Typography variant="h6">
            Log Time - {patientName || patientId}
          </Typography>
        </Stack>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={3}>
            <FormControl fullWidth required>
              <InputLabel>Activity Type</InputLabel>
              <Select
                value={selectedTask}
                label="Activity Type"
                onChange={(e) => setSelectedTask(e.target.value)}
              >
                {PREDEFINED_TASKS.map((task) => (
                  <MenuItem key={task} value={task}>
                    {task}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedTask === 'Other' && (
              <TextField
                fullWidth
                required
                label="Custom Activity"
                value={customTask}
                onChange={(e) => setCustomTask(e.target.value)}
                placeholder="Enter activity description"
              />
            )}

            <TextField
              fullWidth
              required
              label="Begin Time"
              value={displayDateTime}
              onClick={handleOpenPicker}
              InputProps={{
                readOnly: true,
                endAdornment: <CalendarMonth color="action" sx={{ cursor: 'pointer' }} />,
              }}
              InputLabelProps={{
                shrink: true,
              }}
              helperText="When did this activity start?"
              sx={{ cursor: 'pointer', '& input': { cursor: 'pointer' } }}
            />
            <Popover
              open={pickerOpen}
              anchorEl={pickerAnchor}
              onClose={() => setPickerAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 280 }}>
                <TextField
                  type="date"
                  label="Date"
                  value={draftDate}
                  onChange={(e) => setDraftDate(e.target.value)}
                  onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{ '& input': { cursor: 'pointer' } }}
                />
                <div>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>Time</Typography>
                  <TimeInput12h value={draftTime} onChange={setDraftTime} />
                </div>
                <Button variant="contained" onClick={handleSavePicker} fullWidth>
                  Save
                </Button>
              </Paper>
            </Popover>

            <TextField
              fullWidth
              required
              type="number"
              label="Duration (minutes)"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              inputProps={{ min: 1, max: MAX_DURATION_MINUTES }}
              error={durationTooLong}
              helperText={
                durationTooLong
                  ? `Maximum ${MAX_DURATION_MINUTES} minutes per entry`
                  : 'Time spent on this activity'
              }
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional details..."
            />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedTask || !duration || durationTooLong || !beginDate || !beginTimeValue}
          startIcon={loading ? <CircularProgress size={20} /> : <Add />}
        >
          {loading ? 'Logging...' : 'Log Activity'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimeTrackingDialog;
